/**
 * Utility functions for extracting question IDs from workflow steps
 * Shared between useStreamingService and cameraFrame components
 */

/**
 * Extracts question IDs from a workflow step
 * @param step - Workflow step (detect_object or question_id step)
 * @returns Array of question IDs or empty array
 */
export const extractQuestionIdsFromStep = (step: any): string[] => {
  if (!step) return [];
  
  // Handle detect_object step
  if ('type' in step && step.type === 'detect_object') {
    return step.backend?.question_ids || [];
  }
  
  // Handle QuestionIdStep
  if ('question_id' in step) {
    return [step.question_id];
  }
  
  return [];
};

/**
 * Extracts question IDs from config-driven workflow
 * @param currentStep - Current workflow step
 * @param currentIterateStep - Current iterate step (if in iterate mode)
 * @returns Array of question IDs
 */
export const getConfigDrivenQuestionIds = (currentStep: any, currentIterateStep: any): string[] => {
  if (!currentStep) return [];
  
  // Check if we're in an iterate step
  if ('iterate' in currentStep) {
    return extractQuestionIdsFromStep(currentIterateStep);
  }
  
  // Not an iterate step, use currentStep directly
  return extractQuestionIdsFromStep(currentStep);
};

/**
 * Extracts question IDs from legacy inspection workflow
 * @param activeObjectQuestions - Active object questions
 * @param activeQuestion - Active single question
 * @returns Array of question IDs
 */
export const getLegacyQuestionIds = (activeObjectQuestions: any[], activeQuestion: any): string[] => {
  // Handle object-based questions
  if (activeObjectQuestions.length > 0) {
    return activeObjectQuestions
      .map(q => q.questionId)
      .filter((id): id is string => id !== null && id !== undefined);
  }
  
  // Handle single question
  return activeQuestion?.questionId ? [activeQuestion.questionId] : [];
};

/**
 * Extracts active question IDs based on workflow type and current step
 * @param isConfigDrivenWorkflow - Whether this is a config-driven workflow (drumId present)
 * @param currentStep - Current workflow step
 * @param currentIterateStep - Current iterate step (if in iterate mode)
 * @param activeObjectQuestions - Active object questions from legacy workflow
 * @param activeQuestion - Active question from legacy workflow
 * @returns Array of question IDs
 */
export const getActiveQuestionIds = (
  isConfigDrivenWorkflow: boolean,
  currentStep: any,
  currentIterateStep: any,
  activeObjectQuestions: any[],
  activeQuestion: any
): string[] => {
  if (isConfigDrivenWorkflow) {
    return getConfigDrivenQuestionIds(currentStep, currentIterateStep);
  }
  
  return getLegacyQuestionIds(activeObjectQuestions, activeQuestion);
};
